// ViewForm.jsx
import { Modal, ModalBody, ModalHeader, Button } from "reactstrap";
import React from "react";
import {
  Box,
  Grid,
  Typography,
  Paper,
  Stack,
  Chip,
  Avatar,
  Divider,
  ToggleButtonGroup,
  ToggleButton,
} from "@mui/material";
import sanitizeHtml from "../../../../Utils/sanitizeHtml";
import {
  Smartphone,
  Bolt,
  Notes,
  DesktopWindows,
  PhoneIphone,
} from "@mui/icons-material";

// -------- helpers --------
function parseArraySafe(value) {
  if (Array.isArray(value)) return value;
  if (typeof value === "string") {
    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : value ? [value] : [];
    } catch {
      return value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
    }
  }
  return [];
}

const SectionHeader = ({ icon, title, right }) => (
  <Box
    sx={{
      display: "flex",
      alignItems: "center",
      justifyContent: "space-between",
      mb: 2,
    }}
  >
    <Stack direction="row" spacing={1.2} alignItems="center">
      <Avatar
        sx={{
          width: 28,
          height: 28,
          bgcolor: "primary.light",
          color: "primary.main",
        }}
      >
        {icon}
      </Avatar>
      <Typography variant="subtitle1" fontWeight={600}>
        {title}
      </Typography>
    </Stack>
    {right}
  </Box>
);

const NoteCallout = ({ children }) => (
  <Stack
    direction="row"
    spacing={1.5}
    alignItems="flex-start"
    sx={{
      p: 2,
      borderRadius: 2,
      bgcolor: "#f5f8ff",
      border: "1px solid #e5edff",
    }}
  >
    <Avatar
      sx={{ width: 28, height: 28, bgcolor: "#eaf1ff", color: "primary.main" }}
    >
      <Notes fontSize="small" />
    </Avatar>
    <Typography variant="body2" color="text.secondary">
      {children}
    </Typography>
  </Stack>
);

const PhoneFrame = ({ children }) => (
  <Box
    sx={{
      width: { xs: 260, sm: 280 },
      height: { xs: 520, sm: 560 },
      bgcolor: "#f7f7fa",
      borderRadius: "36px",
      border: "10px solid #0d0d12",
      position: "relative",
      boxShadow: 4,
      display: "flex",
      flexDirection: "column",
      overflow: "hidden",
      mx: "auto",
    }}
  >
    <Box sx={{ height: 36, bgcolor: "#f7f7fa" }}>
      <Box
        sx={{
          width: 120,
          height: 22,
          bgcolor: "#dcdce3",
          borderRadius: "12px",
          mx: "auto",
          mt: 1.2,
        }}
      />
    </Box>
    <Box sx={{ flex: 1, overflowY: "auto" }}>{children}</Box>
    <Box
      sx={{
        p: 1.2,
        bgcolor: "#fff",
        borderTop: "1px solid #eee",
        display: "flex",
        gap: 1,
      }}
    >
      <Box
        sx={{
          flex: 1,
          height: 32,
          border: "1px solid #eee",
          borderRadius: 1,
          bgcolor: "#fff",
        }}
      />
      <Button
        size="sm"
        disabled
        className="btn btn-primary"
        style={{ opacity: 0.75 }}
      >
        +
      </Button>
    </Box>
  </Box>
);

// -------- component --------
export default function ViewForm({
  isOpen,
  toggle,
  data = {},
  alignment = "email",
}) {
  const isEmail = data?.communication_type === "email";
  const reqVars = parseArraySafe(data?.required_variables);
  const [viewport, setViewport] = React.useState("desktop");

  // SMS counters
  const smsText = data?.sms_text_template || "";
  const smsChars = smsText.length;
  const smsSegments = Math.max(1, Math.ceil(smsChars / 160));
  const smsCounter = `${smsChars} / 160 chars (${smsSegments} SMS${smsSegments > 1 ? " segments" : ""})`;

  const closeBtn = (
    <Button
      outline
      className="close"
      style={{ marginLeft: "auto" }}
      onClick={toggle}
      type="button"
      aria-label="Close"
      title="Close"
    >
      &times;
    </Button>
  );

  return (
    <Modal
      isOpen={isOpen}
      toggle={toggle}
      size="lg"
      centered
      backdrop="static"
      style={{ maxWidth: 1200, width: "100%" }}
      fade
    >
      <ModalHeader close={closeBtn} id="view-config-title">
        View {isEmail ? "Email" : "SMS"} Template
      </ModalHeader>
      <ModalBody style={{ background: "#f5f7fb" }}>
        {isEmail ? (
          <Grid container spacing={2}>
            {/* LEFT: dynamic tags / components */}
            {/* <Grid item xs={12} md={3}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Typography variant="overline" color="text.secondary">
                  COMPONENTS
                </Typography>

                <Box mt={1.5}>
                  <Typography variant="subtitle2" gutterBottom>
                    Dynamic Tags
                  </Typography>
                  <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                    {(reqVars.length
                      ? reqVars
                      : [
                          "{user_first_name}",
                          "{order_id}",
                          "{tracking_link}",
                          "{current_date}",
                          "{company_name}",
                        ]
                    ).map((t) => (
                      <Chip key={t} label={t} size="small" />
                    ))}
                  </Stack>
                </Box>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Layout Blocks
                </Typography>
                <Stack spacing={1}>
                  <Chip label="Header" size="small" variant="outlined" />
                  <Chip label="Content" size="small" variant="outlined" />
                  <Chip label="CTA Button" size="small" variant="outlined" />
                  <Chip label="Footer" size="small" variant="outlined" />
                </Stack>

                <Divider sx={{ my: 2 }} />
                <Typography variant="subtitle2" gutterBottom>
                  Assets Library
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                  <Chip label="Logo" size="small" />
                  <Chip label="Banner" size="small" />
                </Stack>
              </Paper>
            </Grid> */}

            {/* MIDDLE: preview */}
            <Grid item xs={12} md={6}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                sx={{ mb: 1 }}
              >
                <Typography variant="body2" color="text.secondary">
                  Email Marketing &nbsp;›&nbsp;{" "}
                  <strong>{data?.template_name || "Template"}</strong>
                </Typography>
                <ToggleButtonGroup
                  size="small"
                  exclusive
                  value={viewport}
                  onChange={(_, v) => v && setViewport(v)}
                >
                  <ToggleButton value="desktop">
                    <DesktopWindows fontSize="small" sx={{ mr: 0.5 }} />
                    Desktop
                  </ToggleButton>
                  <ToggleButton value="mobile">
                    <PhoneIphone fontSize="small" sx={{ mr: 0.5 }} />
                    Mobile
                  </ToggleButton>
                </ToggleButtonGroup>
              </Stack>

              <Paper
                elevation={0}
                sx={{
                  p: 3,
                  borderRadius: 2,
                  border: "1px solid #e5e7eb",
                  bgcolor: "#fff",
                  ...(viewport === "mobile" ? { width: 420, mx: "auto" } : {}),
                }}
              >
                <Stack
                  direction="row"
                  spacing={1}
                  alignItems="center"
                  sx={{ mb: 2 }}
                >
                  <Avatar
                    sx={{ bgcolor: "primary.light", color: "primary.main" }}
                  >
                    <Bolt fontSize="small" />
                  </Avatar>
                  <Stack>
                    <Typography variant="caption" color="text.secondary">
                      FROM:
                    </Typography>
                    <Typography variant="body2">
                      {data?.email_from || "hello@brand.io"}
                    </Typography>
                  </Stack>
                </Stack>

                <Typography variant="caption" color="text.secondary">
                  SUBJECT:
                </Typography>
                <Typography
                  variant="h6"
                  sx={{ mb: 2, wordBreak: "break-word" }}
                >
                  {data?.email_subject || "—"}
                </Typography>

                <Paper
                  variant="outlined"
                  sx={{ p: 3, borderRadius: 2, bgcolor: "#fbfbfd" }}
                >
                  <Box
                    dangerouslySetInnerHTML={{
                      __html: sanitizeHtml(
                        (data?.email_html_content &&
                          String(data.email_html_content).trim()) ||
                          "<p>No Preview Available</p>",
                      ),
                    }}
                  />
                </Paper>
              </Paper>
            </Grid>

            {/* RIGHT: details */}
            <Grid item xs={12} md={6}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <SectionHeader
                  icon={<Bolt fontSize="small" />}
                  title="Details"
                />
                <Stack spacing={1.5}>
                  {[
                    ["Template Key", data?.template_key],
                    ["Template Name", data?.template_name],
                    ["Communication Type", data?.communication_type],
                    ["From", data?.email_from],
                    ["BCC", data?.email_bcc],
                    ["Tenant", data?.tenant_id],
                    ["Required Variables", reqVars.join(", ")],
                    [
                      "Created",
                      data?.created_at
                        ? new Date(data.created_at).toLocaleString()
                        : "",
                    ],
                    [
                      "Updated",
                      data?.updated_at
                        ? new Date(data.updated_at).toLocaleString()
                        : "",
                    ],
                  ]
                    .filter(([, v]) => !!(v && String(v).trim()))
                    .map(([label, val]) => (
                      <Stack
                        key={label}
                        direction="row"
                        spacing={1}
                        useFlexGap
                        flexWrap="wrap"
                      >
                        <Typography
                          variant="subtitle2"
                          color="text.secondary"
                          sx={{ minWidth: 140 }}
                        >
                          {label}:
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{ wordBreak: "break-word" }}
                        >
                          {String(val)}
                        </Typography>
                      </Stack>
                    ))}
                </Stack>
              </Paper>
            </Grid>
          </Grid>
        ) : (
          <Grid container spacing={2}>
            {/* LEFT: phone preview */}
            <Grid item xs={12} md={4}>
              <Typography variant="overline" color="text.secondary">
                LIVE PREVIEW
              </Typography>
              <Box mt={1.5}>
                <PhoneFrame>
                  <Box sx={{ p: 2 }}>
                    <Stack spacing={2}>
                      <Stack spacing={1} alignItems="center">
                        <Avatar
                          sx={{ bgcolor: "#e8eef9", color: "primary.main" }}
                        >
                          <Smartphone fontSize="small" />
                        </Avatar>
                        <Typography variant="caption" color="text.secondary">
                          YOUR BUSINESS
                        </Typography>
                      </Stack>

                      <Stack spacing={1}>
                        <Typography
                          variant="caption"
                          align="center"
                          color="text.secondary"
                        >
                          Today 10:45 AM
                        </Typography>
                        <Box sx={{ display: "flex" }}>
                          <Box
                            sx={{
                              ml: "auto",
                              maxWidth: "80%",
                              bgcolor: "primary.main",
                              color: "#fff",
                              p: 1.2,
                              borderRadius: "12px 12px 4px 12px",
                              fontSize: 13,
                              lineHeight: 1.4,
                              whiteSpace: "pre-wrap",
                            }}
                          >
                            {smsText || "No Preview Available"}
                          </Box>
                        </Box>
                        <Typography variant="caption" color="text.secondary">
                          Delivered
                        </Typography>
                      </Stack>
                    </Stack>
                  </Box>
                </PhoneFrame>
              </Box>
            </Grid>

            {/* RIGHT: panels */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <SectionHeader
                      icon={<Bolt fontSize="small" />}
                      title="General Details"
                    />
                    <Grid container spacing={2}>
                      {[
                        ["Template Key", data?.template_key],
                        ["Template Name", data?.template_name],
                        ["Communication Type", data?.communication_type],
                        ["SMS Template ID", data?.sms_template_id],
                        ["SMS Header", data?.sms_header],
                        ["Tenant ID", data?.tenant_id],
                        ["Required Variables", reqVars.join(", ")],
                        [
                          "Created",
                          data?.created_at
                            ? new Date(data.created_at).toLocaleString()
                            : "",
                        ],
                        [
                          "Updated",
                          data?.updated_at
                            ? new Date(data.updated_at).toLocaleString()
                            : "",
                        ],
                      ]
                        .filter(([, v]) => !!(v && String(v).trim()))
                        .map(([label, val]) => (
                          <Grid item xs={12} sm={6} key={label}>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                            >
                              {label}
                            </Typography>
                            <Typography
                              variant="body2"
                              sx={{ wordBreak: "break-word" }}
                            >
                              {String(val)}
                            </Typography>
                          </Grid>
                        ))}
                    </Grid>
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <Paper variant="outlined" sx={{ p: 2.5 }}>
                    <SectionHeader
                      icon={<Smartphone fontSize="small" />}
                      title="Message Body"
                      right={
                        <Typography variant="caption" color="text.secondary">
                          {smsCounter}
                        </Typography>
                      }
                    />
                    <Typography
                      variant="subtitle2"
                      color="text.secondary"
                      sx={{ mb: 1 }}
                    >
                      {data?.sms_header || "—"}
                    </Typography>
                    <Box
                      sx={{
                        border: "1px dashed",
                        borderColor: "divider",
                        borderRadius: 1,
                        p: 1.5,
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-word",
                        bgcolor: "background.default",
                      }}
                    >
                      {smsText || "No message available."}
                    </Box>

                    <Box mt={2}>
                      <NoteCallout>
                        Messages over 160 characters may be split into multiple
                        segments and may incur extra costs. Keep variables short
                        to avoid unexpected splitting.
                      </NoteCallout>
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </Grid>
          </Grid>
        )}
      </ModalBody>
    </Modal>
  );
}
